import deriveBaseUrl from './deriveBaseUrl.ts';
import escapeRegExp from './escapeRegExp.ts';
import getCurrentHostname from './getCurrentHostname.ts';

const IMAGE_PROXY_PATH = '/v2/api/image-proxy?url=';
const URL_TAIL = `[^\\s"'()]*`;

const PROXIED_HOST_PATTERNS = [
  'cdn\\.raceroster\\.com',
  'timer-cdn\\.raceroster\\.com',
  'timer-cdn\\.[^.]+\\.rostrr\\.net',
];

const ABSOLUTE_PROXY_URL_REGEX = new RegExp(
  `https?://[^\\s"'()/]+${escapeRegExp(IMAGE_PROXY_PATH)}${URL_TAIL}`,
  'g'
);

const proxiedHostRegexCache = new Map<string, RegExp>();

const getProxiedHostRegex = (baseDomain: string): RegExp => {
  const cached = proxiedHostRegexCache.get(baseDomain);
  if (cached) return cached;

  const hosts = [...PROXIED_HOST_PATTERNS, escapeRegExp(baseDomain)].join('|');
  const regex = new RegExp(`https?://(?:${hosts})(?=[:/?#]|$)${URL_TAIL}`, 'g');
  proxiedHostRegexCache.set(baseDomain, regex);

  return regex;
};

const rehostProxyUrls = (content: string, origin: string, host: string): string =>
  content.replace(ABSOLUTE_PROXY_URL_REGEX, (proxyUrl: string) => {
    const { hostname, pathname, search, hash } = new URL(proxyUrl);

    return hostname === host ? `${origin}${pathname}${search}${hash}` : proxyUrl;
  });

const isAlreadyProxied = (url: string, offset: number, content: string): boolean =>
  content.startsWith(IMAGE_PROXY_PATH, offset - IMAGE_PROXY_PATH.length) ||
  url.includes(IMAGE_PROXY_PATH);

const proxyAssetUrls = (content: string, origin: string, host: string): string =>
  content.replace(
    getProxiedHostRegex(deriveBaseUrl(host)),
    (url: string, offset: number, fullContent: string) =>
      isAlreadyProxied(url, offset, fullContent) ? url : `${origin}${IMAGE_PROXY_PATH}${url}`
  );

const normalizeImageUrls = (htmlContent: string): string => {
  const origin = getCurrentHostname();
  const { hostname } = new URL(origin);

  return proxyAssetUrls(rehostProxyUrls(htmlContent, origin, hostname), origin, hostname);
};

export default normalizeImageUrls;
